/**
 * AUTO-GENERATED by scripts/generate-cvent-plan.mjs — DO NOT EDIT BY HAND.
 *
 * Source of truth: src/analytics/cvent/actions/*.json.
 * Regenerate with: pnpm attendee-activities:generate-plan
 */
import {
  canPublish,
  getMissingIdentityKeys,
  isEnabled as isCventAnalyticsEnabled,
  logSkippedFact,
  track,
  type CventActivitiesBaseProperties
} from './core';
import {
  ChatMessageSent,
  type ChatMessageSentProps,
  PollAnswerSubmitted,
  type PollAnswerSubmittedProps,
  QuestionAsked,
  type QuestionAskedProps,
  ReactionAdded,
  type ReactionAddedProps
} from './models';

export interface GeneratedTrackMethodDeps {
  collectBaseProperties: () => CventActivitiesBaseProperties;
  initialize: () => void;
}

export interface GeneratedTrackMethods {
  trackChatMessageSent: (payload: Omit<ChatMessageSentProps, 'objectId'>) => boolean;
  trackPollAnswerSubmitted: (payload: Omit<PollAnswerSubmittedProps, 'objectId'>) => boolean;
  trackQuestionAsked: (payload: Omit<QuestionAskedProps, 'objectId'>) => boolean;
  trackReactionAdded: (payload: Omit<ReactionAddedProps, 'objectId'>) => boolean;
}

export const GENERATED_TRACK_METHODS_BY_ACTIVITY_TYPE = {
  'session.chatMessageSent': 'trackChatMessageSent',
  'session.poll_answer_submitted': 'trackPollAnswerSubmitted',
  'session.question_asked': 'trackQuestionAsked',
  'session.reactionAdded': 'trackReactionAdded'
} as const;

export function createGeneratedTrackMethods(deps: GeneratedTrackMethodDeps): GeneratedTrackMethods {
  function trackChatMessageSent(payload: Omit<ChatMessageSentProps, 'objectId'>): boolean {
    const baseProps = deps.collectBaseProperties();
    if (!canPublish(baseProps)) {
      logSkippedFact(
        baseProps,
        'session.chatMessageSent',
        'incomplete Cvent identity',
        getMissingIdentityKeys(baseProps)
      );
      return false;
    }

    if (!isCventAnalyticsEnabled()) {
      deps.initialize();
      if (!isCventAnalyticsEnabled()) {
        logSkippedFact(baseProps, 'session.chatMessageSent', 'publishing not enabled');
        return false;
      }
    }

    const objectId = payload.sessionId;
    if (!objectId) {
      logSkippedFact(baseProps, 'session.chatMessageSent', 'missing sessionId');
      return false;
    }

    track(
      new ChatMessageSent({
        objectId,
        ...payload
      })
    );

    return true;
  }

  function trackPollAnswerSubmitted(payload: Omit<PollAnswerSubmittedProps, 'objectId'>): boolean {
    const baseProps = deps.collectBaseProperties();
    if (!canPublish(baseProps)) {
      logSkippedFact(
        baseProps,
        'session.poll_answer_submitted',
        'incomplete Cvent identity',
        getMissingIdentityKeys(baseProps)
      );
      return false;
    }

    if (!isCventAnalyticsEnabled()) {
      deps.initialize();
      if (!isCventAnalyticsEnabled()) {
        logSkippedFact(baseProps, 'session.poll_answer_submitted', 'publishing not enabled');
        return false;
      }
    }

    const objectId = payload.sessionId;
    if (!objectId) {
      logSkippedFact(baseProps, 'session.poll_answer_submitted', 'missing sessionId');
      return false;
    }

    track(
      new PollAnswerSubmitted({
        objectId,
        ...payload
      })
    );

    return true;
  }

  function trackQuestionAsked(payload: Omit<QuestionAskedProps, 'objectId'>): boolean {
    const baseProps = deps.collectBaseProperties();
    if (!canPublish(baseProps)) {
      logSkippedFact(
        baseProps,
        'session.question_asked',
        'incomplete Cvent identity',
        getMissingIdentityKeys(baseProps)
      );
      return false;
    }

    if (!isCventAnalyticsEnabled()) {
      deps.initialize();
      if (!isCventAnalyticsEnabled()) {
        logSkippedFact(baseProps, 'session.question_asked', 'publishing not enabled');
        return false;
      }
    }

    const objectId = payload.sessionId;
    if (!objectId) {
      logSkippedFact(baseProps, 'session.question_asked', 'missing sessionId');
      return false;
    }

    track(
      new QuestionAsked({
        objectId,
        ...payload
      })
    );

    return true;
  }

  function trackReactionAdded(payload: Omit<ReactionAddedProps, 'objectId'>): boolean {
    const baseProps = deps.collectBaseProperties();
    if (!canPublish(baseProps)) {
      logSkippedFact(
        baseProps,
        'session.reactionAdded',
        'incomplete Cvent identity',
        getMissingIdentityKeys(baseProps)
      );
      return false;
    }

    if (!isCventAnalyticsEnabled()) {
      deps.initialize();
      if (!isCventAnalyticsEnabled()) {
        logSkippedFact(baseProps, 'session.reactionAdded', 'publishing not enabled');
        return false;
      }
    }

    const objectId = payload.sessionId;
    if (!objectId) {
      logSkippedFact(baseProps, 'session.reactionAdded', 'missing sessionId');
      return false;
    }

    track(
      new ReactionAdded({
        objectId,
        ...payload
      })
    );

    return true;
  }

  return {
    trackChatMessageSent,
    trackPollAnswerSubmitted,
    trackQuestionAsked,
    trackReactionAdded
  };
}
